import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@heroui/button';
import { Card, CardBody } from '@heroui/card';
import { Spinner } from '@heroui/spinner';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';
import { useAuth } from '../../contexts/AuthContext';
import oauthService from '../../services/oauth';

const OAuthCallback: React.FC = () => {
  const pageTitle = 'Signing In';
  const pageDescription = 'Completing your sign-in with the selected provider.';
  const { provider } = useParams<{ provider: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { oauthLogin } = useAuth();

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>(
    'loading'
  );
  const [error, setError] = useState<string>('');
  const hasExecuted = useRef(false);

  const handleOAuthCallback = useCallback(async () => {
    try {
      // Check for error in URL params (OAuth error response)
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      if (error) {
        let errorMessage = 'Authentication was cancelled or failed';

        switch (error) {
          case 'access_denied':
            errorMessage = 'Access was denied. Please try again.';
            break;
          case 'invalid_request':
            errorMessage = 'Invalid authentication request.';
            break;
          case 'invalid_client':
            errorMessage = 'Application not properly configured.';
            break;
          case 'invalid_grant':
            errorMessage = 'Authentication grant is invalid or expired.';
            break;
          case 'unsupported_response_type':
            errorMessage = 'Unsupported authentication method.';
            break;
          default:
            errorMessage = errorDescription || `Authentication error: ${error}`;
        }

        throw new Error(errorMessage);
      }

      if (!provider) {
        throw new Error('No OAuth provider specified');
      }

      // Validate provider
      const oauthProvider = oauthService.getProvider(provider);
      if (!oauthProvider) {
        throw new Error(`Unsupported OAuth provider: ${provider}`);
      }

      setStatus('loading');

      // Get callback data instead of making direct token request (fixes CORS)
      const callbackData = oauthService.getCallbackData();

      if (!callbackData) {
        throw new Error('No authorization code received from provider');
      }

      if (callbackData.provider !== provider) {
        throw new Error('Provider mismatch in callback');
      }

      // Send the authorization code and verifier to our backend
      // The backend will handle the token exchange with proper CORS handling
      await oauthLogin({
        provider,
        code: callbackData.code,
        codeVerifier: callbackData.codeVerifier,
        redirectUri: oauthProvider.redirectUri,
      });

      setStatus('success');

      // Get return URL: cookie first, then from state param (survives redirect when cookie is lost)
      const stateParam = searchParams.get('state');
      const returnUrl =
        oauthService.getReturnUrl(provider) ||
        oauthService.getReturnUrlFromState(stateParam) ||
        '/account';

      // Clean up OAuth state
      oauthService.cleanup();

      // Show success message
      toast.success(`Successfully signed in with ${oauthProvider.name}!`);

      // Redirect after a brief delay
      setTimeout(() => {
        navigate(returnUrl, { replace: true });
      }, 1500);
    } catch (error: any) {
      setError(error.message || 'Authentication failed');
      setStatus('error');

      // Clean up OAuth state on error
      if (provider) {
        oauthService.clearTokens(provider);
      }

      toast.error(error.message || 'Authentication failed');
    }
  }, [navigate, oauthLogin, provider, searchParams]);

  useEffect(() => {
    // Prevent double execution
    if (hasExecuted.current) {
      return;
    }
    hasExecuted.current = true;

    handleOAuthCallback();
  }, [handleOAuthCallback]);

  const handleRetry = () => {
    if (provider) {
      const returnUrl = oauthService.getReturnUrl(provider);
      oauthService.initiateLogin(provider, returnUrl);
    }
  };

  const handleGoBack = () => {
    navigate('/login', { replace: true });
  };

  const getProviderInfo = () => {
    if (!provider) return null;
    return oauthService.getProvider(provider);
  };

  const providerInfo = getProviderInfo();

  return (
    <>
      <SEOHead title={pageTitle} description={pageDescription} />
      <div className='min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8'>
        <div className='max-w-md w-full space-y-8'>
          <Card>
            <CardBody className='text-center py-8'>
              {status === 'loading' && (
                <>
                  <Spinner size='lg' className='mb-4' />
                  <h2 className='text-xl font-semibold text-gray-900 mb-2'>
                    Completing sign-in...
                  </h2>
                  <p className='text-gray-600'>
                    {providerInfo
                      ? `Authenticating with ${providerInfo.name}`
                      : 'Processing authentication'}
                  </p>
                </>
              )}

              {status === 'success' && (
                <>
                  <CheckCircleIcon className='w-16 h-16 text-success-500 mx-auto mb-4' />
                  <h2 className='text-xl font-semibold text-gray-900 mb-2'>
                    Sign-in successful!
                  </h2>
                  <p className='text-gray-600'>
                    {providerInfo
                      ? `Welcome! You've successfully signed in with ${providerInfo.name}.`
                      : 'Welcome! Redirecting you now...'}
                  </p>
                  <div className='mt-4'>
                    <div className='inline-flex items-center space-x-2'>
                      <Spinner size='sm' />
                      <span className='text-sm text-gray-500'>
                        Redirecting...
                      </span>
                    </div>
                  </div>
                </>
              )}

              {status === 'error' && (
                <>
                  <XCircleIcon className='w-16 h-16 text-danger-500 mx-auto mb-4' />
                  <h2 className='text-xl font-semibold text-gray-900 mb-2'>
                    Sign-in failed
                  </h2>
                  <p className='text-danger-600 mb-4'>{error}</p>

                  <div className='space-y-3'>
                    {providerInfo && (
                      <Button
                        color='primary'
                        onClick={handleRetry}
                        className='w-full'
                      >
                        Try again with {providerInfo.name}
                      </Button>
                    )}

                    <Button
                      variant='bordered'
                      onClick={handleGoBack}
                      className='w-full'
                    >
                      Back to sign in
                    </Button>
                  </div>
                </>
              )}
            </CardBody>
          </Card>

          {status === 'error' && (
            <Card className='mt-4'>
              <CardBody className='bg-warning-50'>
                <div className='flex items-start space-x-3'>
                  <ExclamationTriangleIcon className='w-6 h-6 text-warning-600 flex-shrink-0 mt-0.5' />
                  <div>
                    <h3 className='text-sm font-medium text-warning-800'>
                      Troubleshooting Tips
                    </h3>
                    <div className='mt-2 text-sm text-warning-700'>
                      <ul className='list-disc list-inside space-y-1'>
                        <li>Make sure you're using a supported browser</li>
                        <li>Check that cookies and JavaScript are enabled</li>
                        <li>Try clearing your browser cache and cookies</li>
                        <li>
                          If the problem persists, try signing in with email
                          instead
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}
        </div>
      </div>
    </>
  );
};

export default OAuthCallback;
